import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  Stack,
  TextField,
} from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { Act } from "../../../../types/act";
import apiClient from "../../../../utils/apiClient";
import { PdfViewer } from "../../../../components/PdfViewer";
import ActStatus from "../../../../enum/ActStatus.enum";

interface ArmeniaActDialogProps {
  act: Act;
  isAdmin?: boolean;
  onClose: () => void;
  onReload: () => void;
}

export const ArmeniaActDialog: React.FC<ArmeniaActDialogProps> = ({
  act,
  isAdmin = false,
  onClose,
  onReload,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | undefined>();
  const [fileName, setFileName] = useState<string>();
  const [error, setError] = useState<string>();

  const [disagreementOpen, setDisagreementOpen] = useState(false);
  const [disagreementText, setDisagreementText] = useState("");

  const downloadAct = async () => {
    try {
      setIsLoading(true);
      setError(undefined);

      const response = await apiClient.get(`/acts/${act.id}/print/act`, {
        responseType: "arraybuffer",
      });

      if (response?.data) {
        setPdfBlob(new Blob([response.data], { type: "application/pdf" }));
      }

      const contentDisposition = response.headers["content-disposition"];
      let name = `act_${act.id}.pdf`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match && match[1]) {
          name = decodeURIComponent(match[1]);
        }
      }

      setFileName(name);
    } catch (e) {
      console.error("Ошибка загрузки акта:", e);
      setError(t("armeniaAct.dialog.loadError"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (act?.id) {
      downloadAct();
    }
  }, [act?.id]);

  const uploadSignedAct = async (file: File) => {
    try {
      setActionPending(true);

      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post(
        `/acts/${act.id}/signed-file`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        onReload();
        onClose();
      }
    } catch (e) {
      console.error("Ошибка загрузки подписанного акта:", e);
      setError(t("armeniaAct.dialog.uploadSignedError"));
    } finally {
      setActionPending(false);
    }
  };

  const sendDisagreement = async () => {
    if (!disagreementText.trim()) {
      return;
    }

    try {
      setActionPending(true);

      const response = await apiClient.post(`/acts/${act.id}/disagreement`, {
        comment: disagreementText.trim(),
      });

      if (response.data.success) {
        setDisagreementOpen(false);
        setDisagreementText("");
        onReload();
        onClose();
      }
    } catch (e) {
      console.error("Ошибка отправки несогласия:", e);
      setError(t("armeniaAct.dialog.disagreementSendError"));
    } finally {
      setActionPending(false);
    }
  };

  const regenerateAct = async () => {
    try {
      setActionPending(true);

      const response = await apiClient.post(`/acts/${act.id}/regenerate`);

      if (response.data.success) {
        onReload();
        onClose();
      }
    } catch (e) {
      console.error("Ошибка перегенерации акта:", e);
      setError(t("armeniaAct.dialog.regenerateError"));
    } finally {
      setActionPending(false);
    }
  };

  const footer = useMemo(() => {
    const status = act?.status;

    if (status === ActStatus.SIGNED) {
      const approvedAt = act?.approved_at
        ? dayjs(act.approved_at).format("DD.MM.YYYY HH:mm:ss")
        : "";

      return (
        <Stack spacing={1} direction="row" alignItems="center" sx={{ pr: 2 }}>
          <TaskAltIcon color="success" />
          <ListItemText
            primary={t("armeniaAct.dialog.signedUploaded")}
            secondary={approvedAt}
          />
        </Stack>
      );
    }

    if (isAdmin) {
      return (
        <Stack direction="row" spacing={1}>
          {status === ActStatus.DISAGREEMENT && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={regenerateAct}
              loading={actionPending}
            >
              {t("armeniaAct.dialog.regenerate")}
            </Button>
          )}
        </Stack>
      );
    }

    if (status === ActStatus.DRAFT || status === ActStatus.EXPIRED) {
      return (
        <Stack direction="row" spacing={1}>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".pdf,.doc,.docx,image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];

              if (file) {
                uploadSignedAct(file);
              }

              e.target.value = "";
            }}
          />

          <Button
            variant="contained"
            color="primary"
            startIcon={<FileUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            loading={actionPending}
          >
            {t("armeniaAct.dialog.uploadSigned")}
          </Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<ReportProblemOutlinedIcon />}
            onClick={() => setDisagreementOpen(true)}
            disabled={actionPending}
          >
            {t("armeniaAct.dialog.disagree")}
          </Button>
        </Stack>
      );
    }

    if (status === ActStatus.DISAGREEMENT) {
      return (
        <Stack spacing={1} direction="row" alignItems="center" sx={{ pr: 2 }}>
          <ReportProblemOutlinedIcon color="error" />
          <ListItemText
            primary={t("armeniaAct.dialog.disagreementSent")}
            secondary={t("armeniaAct.dialog.waitAdmin")}
          />
        </Stack>
      );
    }

    return null;
  }, [act, actionPending, isAdmin, t]);

  return (
    <>
      <PdfViewer
        open={act?.id > 0}
        onClose={() => {
          onClose();
          setPdfBlob(undefined);
          setFileName(undefined);
          setError(undefined);
        }}
        title={
          isLoading
            ? t("common.loading")
            : (fileName ??
              t("armeniaAct.dialog.title", {
                number: act?.act_number ?? act?.id ?? "",
              }))
        }
        pdfBlob={pdfBlob}
        loading={isLoading}
        footer={footer}
        error={error}
      />

      <Dialog
        open={disagreementOpen}
        onClose={() => !actionPending && setDisagreementOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("armeniaAct.dialog.disagreementTitle")}</DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label={t("armeniaAct.dialog.disagreementReason")}
              value={disagreementText}
              onChange={(e) => setDisagreementText(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              autoFocus
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDisagreementOpen(false)}
            disabled={actionPending}
          >
            {t("common.cancel")}
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={sendDisagreement}
            loading={actionPending}
            disabled={!disagreementText.trim()}
          >
            {t("common.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
